import {
  createListenerMiddleware,
  PayloadAction,
  PayloadActionCreator,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { addUnreadMessages } from '../alerts/chatSlice';
import { addNewAlert } from '../alerts/incidentsSlice';
import {
  addConversation,
  setConversationUnread,
} from '../conversations/conversationsSlice';
import { addLocation } from '../emergencyCalls/emergencyCallsSlice';
import { CHAT_TAB } from '../eventPane/eventPaneTabs';
import { addRCSConversation, setRcsUnseenMessageStatus } from '../rcs/rcsSlice';
import { setConversationUnread as setTF911ConversationUnread } from '../textFrom911/textFrom911Slice';
import { AudioNotificationsGlobalState } from './audioNotificationTypes';
import {
  playNotification,
  setSuppressedAudio,
} from './audioNotificationsSlice';

// We have to use a middleware, here, because addCase(actionType) in a
// sliceReducer doesn't provide access to global state and dispatch, which the
// playNotification ActionThunk requires. We still default to addCase whereever
// possible; see the AudioNotificationSlice.
export const audioNotificationsListener = createListenerMiddleware<
  AudioNotificationsGlobalState,
  ThunkDispatch<
    AudioNotificationsGlobalState,
    AudioNotificationsGlobalState,
    PayloadAction<{ caller_id: string } | string>
  >
>();

// Listen for new location to be added to the queue, and dispatch the "new
// location notification" action.
audioNotificationsListener.startListening({
  predicate: (
    action,
  ): action is {
    type: 'ADD_LOCATION_TO_QUEUE';
    loc: { caller_id: string };
  } => {
    return action.type === 'ADD_LOCATION_TO_QUEUE';
  },
  effect: async (action, { dispatch }) => {
    dispatch(playNotification({ category: 'calls', id: action.loc.caller_id }));
  },
});

// Collaborator locations messages arrive this way
audioNotificationsListener.startListening({
  actionCreator: addLocation,
  effect: async (action, { dispatch }) => {
    dispatch(
      playNotification({ category: 'calls', id: action.payload.caller_id }),
    );
  },
});

// Alerts
audioNotificationsListener.startListening({
  actionCreator: addNewAlert,
  effect: async (action, { dispatch }) => {
    dispatch(
      playNotification({ category: 'alerts', id: action.payload.alert_id }),
    );
  },
});

// Alert conversations:
audioNotificationsListener.startListening({
  actionCreator: addUnreadMessages as unknown as PayloadActionCreator<
    { alertID: string; messageID: string },
    'chat/addUnreadMessages'
  >,
  effect: async (action, { dispatch }) => {
    dispatch(
      playNotification({
        category: 'alertMessages',
        id: `${action.payload.alertID}.${action.payload.messageID}`,
      }),
    );
  },
});

// TF911
audioNotificationsListener.startListening({
  actionCreator: setTF911ConversationUnread as unknown as PayloadActionCreator<
    { callerID: string; messageID?: string; hasUnreadMessages: boolean },
    'textFrom911/setConversationUnread'
  >,
  effect: async (action, { dispatch }) => {
    if (action.payload.hasUnreadMessages && action.payload.messageID) {
      dispatch(
        playNotification({
          category: 'textMessages',
          id: `${action.payload.callerID}.${action.payload.messageID}`,
        }),
      );
    }
  },
});

// Conversations:
// Follow up messages in existing conversations
audioNotificationsListener.startListening({
  actionCreator: setConversationUnread,
  effect: async (action, { dispatch }) => {
    if (action.payload.isUnread && action.payload.messageID) {
      dispatch(
        playNotification({
          category: 'textMessages',
          id: `${action.payload.callerID}.${action.payload.messageID}`,
        }),
      );
    }
  },
});

// Initial messages in new conversations for existing calls
audioNotificationsListener.startListening({
  actionCreator: addConversation,
  effect: async (action, { dispatch, getState }) => {
    const { caller_id, conversation_id } = action.payload;
    if (caller_id && getState().conversations[caller_id]) {
      dispatch(
        playNotification({
          category: 'textMessages',
          id: `${caller_id}.${conversation_id}`,
          isInitial: true,
        }),
      );
    }
  },
});

// RCS:
audioNotificationsListener.startListening({
  actionCreator: setRcsUnseenMessageStatus as unknown as PayloadActionCreator<
    { callerID: string; messageID?: string; unseenMessage: boolean },
    'rcs/setRcsUnseenMessageStatus'
  >,
  effect: async (action, { dispatch }) => {
    if (action.payload.unseenMessage && action.payload.messageID) {
      dispatch(
        playNotification({
          category: 'textMessages',
          id: `${action.payload.callerID}.${action.payload.messageID}`,
        }),
      );
    }
  },
});

// Initial messages in new RCS conversations for existing calls
audioNotificationsListener.startListening({
  actionCreator: addRCSConversation as unknown as PayloadActionCreator<
    { caller_id: string; conversation_id: string },
    'rcs/addRCSConversation'
  >,
  effect: async (action, { dispatch, getState }) => {
    const { caller_id, conversation_id } = action.payload;
    if (caller_id && getState().rcs.conversations[caller_id]) {
      dispatch(
        playNotification({
          category: 'textMessages',
          id: `${caller_id}.${conversation_id}`,
          isInitial: true,
        }),
      );
    }
  },
});

// Set suppressed audio whenever a call is selected and the chat pane is open
audioNotificationsListener.startListening({
  predicate: (_action, currentState, previousState) => {
    // Check if selectedCallerID or isChatPaneOpen has changed
    const prevCallerID = previousState.emergencyCalls.selectedCallerID;
    const newCallerID = currentState.emergencyCalls.selectedCallerID;

    const prevChatPaneOpen = previousState.eventPane.selectedTab === CHAT_TAB;
    const newChatPaneOpen = currentState.eventPane.selectedTab === CHAT_TAB;

    return prevCallerID !== newCallerID || prevChatPaneOpen !== newChatPaneOpen;
  },
  effect: async (_, { dispatch, getState }) => {
    const state = getState();
    const selectedCallerID = state.emergencyCalls.selectedCallerID;
    const isChatPaneOpen = state.eventPane.selectedTab === CHAT_TAB;

    dispatch(
      setSuppressedAudio(
        !!selectedCallerID && isChatPaneOpen ? selectedCallerID : '',
      ),
    );
  },
});
